import React, { useCallback } from 'react';

import {
  ArrayInput,
  AutocompleteInput,
  Form,
  ReferenceInput,
  required,
  SimpleFormIterator,
  useNotify,
  useRecordContext,
  useRefresh,
  useSimpleFormIterator,
  useSimpleFormIteratorItem,
  useUpdate,
} from 'react-admin';

import { FieldValues } from 'react-hook-form';
import { isNil } from 'lodash';

import { Add, RemoveCircleOutline } from '@mui/icons-material';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import useDialogStatus from '../../hooks/useDialogStatus';
import authenticatorTypes from '../../constants/authenticatorTypes';
import errorNotification from '../../utilities/errorNotification';
import resourceNames from '../../constants/resourceNames';

const CustomAddMemberButton = () => {
  const { add } = useSimpleFormIterator();
  return (
    <Button
      startIcon={<Add />}
      size="small"
      variant="text"
      onClick={() => add()}
    >
      Add api key
    </Button>
  );
};

const CustomRemoveMemberButton = () => {
  const { remove } = useSimpleFormIterator();
  const { index } = useSimpleFormIteratorItem();
  return (
    <IconButton onClick={() => remove(index)}>
      <RemoveCircleOutline />
    </IconButton>
  );
};

const AssignApiKeyPermission = () => {
  const { open, openDialog, closeDialog } = useDialogStatus();
  const record = useRecordContext();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  const onClickAssignApiKeys = useCallback((data: FieldValues) => {
    if (!record) return;
    update(
      resourceNames.PERMISSION_ROLES,
      {
        id: `${record.id}/attach`,
        data: {
          authenticators: (data.authenticators as { id: string }[])
            .filter(({ id }) => !isNil(id))
            .map(({ id }) => ({ id, type: authenticatorTypes.API_KEY })),
        },
      },
      {
        onSuccess: () => {
          notify('API Key roles added', { type: 'info' });
          closeDialog();
          refresh();
        },
        onError: (e) => {
          notify(errorNotification(e), { type: 'error' });
        },
      },
    );
  }, [closeDialog, notify, record, refresh, update]);

  if (!record) return null;

  return (
    <>
      <Dialog open={open}>
        <Form onSubmit={onClickAssignApiKeys}>
          <DialogTitle fontWeight="bold">Assign api keys</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={6}>
              <Box display="flex" gap={1}>
                <Typography>{'Select the api key you want to assign '}</Typography>
                <Typography fontWeight="bold">
                  {record.name}
                </Typography>
                <Typography>role to.</Typography>
              </Box>
              <ArrayInput source="authenticators" label={false} defaultValue={[{}]}>
                <SimpleFormIterator
                  inline
                  sx={{ '.RaSimpleFormIterator-form': { marginY: 2 } }}
                  disableReordering
                  addButton={<CustomAddMemberButton />}
                  removeButton={<CustomRemoveMemberButton />}
                  disableClear
                >
                  <ReferenceInput source="id" reference={resourceNames.API_KEYS} filter={{ active: true }}>
                    <AutocompleteInput
                      label="Api Key"
                      fullWidth
                      filterSelectedOptions={false}
                      filterToQuery={(searchText) => ({ clientId: searchText })}
                      optionText={(item: {clientId: string}) => item.clientId}
                      validate={[required()]}
                    />
                  </ReferenceInput>
                </SimpleFormIterator>
              </ArrayInput>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button type="submit" variant="contained">Confirm</Button>
          </DialogActions>
        </Form>

      </Dialog>
      <Button
        startIcon={<Add />}
        size="small"
        variant="contained"
        onClick={openDialog}
      >
        Assign API Key
      </Button>
    </>
  );
};

export default AssignApiKeyPermission;
